import React, { useEffect, useState } from "react";
import "./PlombiersList.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const PlombiersList = () => {
  const [plombiers, setPlombiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [sortField, setSortField] = useState("chiffreAffaire");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Date filtering states
  const [filterType, setFilterType] = useState("all"); // "all", "day", "week", "month", "year"
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedWeekStart, setSelectedWeekStart] = useState("");
  const [selectedWeekEnd, setSelectedWeekEnd] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchPlombiers();
    fetchGroups();
    fetchAvailableYears();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPlombiers();
    fetchGroups();
  }, [filterType, selectedDate, selectedWeekStart, selectedWeekEnd, selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAvailableYears = async () => {
    try {
      const response = await fetch(`${API_URL}/api/plombiers/years`);
      if (response.ok) {
        const years = await response.json();
        setAvailableYears(years);
      }
    } catch (err) {
      console.error("Error fetching available years:", err);
    }
  };

  const fetchPlombiers = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/plombiers`;
      const params = new URLSearchParams();
      
      if (filterType === "day" && selectedDate) {
        params.append("date", selectedDate);
        params.append("filterType", "day");
      } else if (filterType === "week" && selectedWeekStart && selectedWeekEnd) {
        params.append("weekStart", selectedWeekStart);
        params.append("weekEnd", selectedWeekEnd);
        params.append("filterType", "week");
      } else if (filterType === "month" && selectedMonth && selectedYear) {
        params.append("month", selectedMonth);
        params.append("year", selectedYear);
        params.append("filterType", "month");
      } else if (filterType === "year" && selectedYear) {
        params.append("year", selectedYear);
        params.append("filterType", "year");
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error("Erreur lors du chargement");
      const data = await response.json();
      setPlombiers(data);
    } catch (err) {
      console.error("Error fetching plombiers:", err);
      setError(err.message);
      setPlombiers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      let url = `${API_URL}/api/plombiers/groups`;
      const params = new URLSearchParams();

      if (filterType === "day" && selectedDate) {
        params.append("date", selectedDate);
        params.append("filterType", "day");
      } else if (filterType === "week" && selectedWeekStart && selectedWeekEnd) {
        params.append("weekStart", selectedWeekStart);
        params.append("weekEnd", selectedWeekEnd);
        params.append("filterType", "week");
      } else if (filterType === "month" && selectedMonth && selectedYear) {
        params.append("month", selectedMonth);
        params.append("year", selectedYear);
        params.append("filterType", "month");
      } else if (filterType === "year" && selectedYear) {
        params.append("year", selectedYear);
        params.append("filterType", "year");
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Erreur lors du chargement des groupes");
      const data = await response.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching plumber groups:", err);
      // Don't surface as main error to avoid blocking singles view
      setGroups([]);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const sortedAndFilteredPlombiers = React.useMemo(() => {
    let filtered = plombiers;
    
    if (searchTerm) {
      filtered = plombiers.filter(p =>
        p.nom.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const multiplier = sortDirection === "asc" ? 1 : -1;
      
      if (typeof aVal === "string") {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return (aVal - bVal) * multiplier;
    });

    return sorted;
  }, [plombiers, sortField, sortDirection, searchTerm]);

  // Calculate totals
  const totals = React.useMemo(() => {
    return sortedAndFilteredPlombiers.reduce((acc, p) => {
      acc.chantiers += p.nombreChantiers;
      acc.chiffre += p.chiffreAffaire;
      acc.marge += p.marge;
      return acc;
    }, { chantiers: 0, chiffre: 0, marge: 0 });
  }, [sortedAndFilteredPlombiers]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const getMargeColor = (percent) => {
    if (percent >= 30) return "#10b981"; // Green
    if (percent >= 20) return "#f59e0b"; // Orange
    return "#ef4444"; // Red
  };

  const getFilterLabel = () => {
    switch (filterType) {
      case "day":
        return selectedDate ? `Jour: ${new Date(selectedDate).toLocaleDateString('fr-FR')}` : "Choisir un jour";
      case "week":
        return selectedWeekStart && selectedWeekEnd 
          ? `Semaine: ${new Date(selectedWeekStart).toLocaleDateString('fr-FR')} - ${new Date(selectedWeekEnd).toLocaleDateString('fr-FR')}` 
          : "Choisir une semaine";
      case "month":
        return selectedMonth && selectedYear ? `Mois: ${getMonthName(selectedMonth)} ${selectedYear}` : "Choisir un mois";
      case "year":
        return selectedYear ? `Année: ${selectedYear}` : "Choisir une année";
      default:
        return "Toutes les données";
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[parseInt(monthNumber) - 1] || '';
  };

  // Helper functions for week calculations
  const getWeekStartDate = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  const getWeekEndDate = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) + 6; // Sunday as last day
    const sunday = new Date(d.setDate(diff));
    return sunday.toISOString().split('T')[0];
  };

  const handleWeekDateChange = (date) => {
    if (date) {
      const weekStart = getWeekStartDate(date);
      const weekEnd = getWeekEndDate(date);
      setSelectedWeekStart(weekStart);
      setSelectedWeekEnd(weekEnd);
    } else {
      setSelectedWeekStart("");
      setSelectedWeekEnd("");
    }
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    // Reset filter values when changing type
    if (type !== "day") setSelectedDate("");
    if (type !== "week") {
      setSelectedWeekStart("");
      setSelectedWeekEnd("");
    }
    if (type !== "month") setSelectedMonth("");
    if (type === "all") {
      setSelectedDate("");
      setSelectedWeekStart("");
      setSelectedWeekEnd("");
      setSelectedMonth("");
      setSelectedYear(new Date().getFullYear().toString());
    }
  };

  if (loading) {
    return (
      <div className="plombiers-loading">
        <div className="loading-spinner"></div>
        <p>Chargement des plombiers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="plombiers-error">
        <p>❌ {error}</p>
        <button onClick={fetchPlombiers} className="retry-btn">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="plombiers-container">
      <div className="plombiers-header">
        <div className="header-content">
          <h1 className="plombiers-title">
            <span className="title-icon">🔧</span>
            Gestion des Plombiers
          </h1>
          <p className="plombiers-subtitle">
            Analyse de performance et statistiques
          </p>
        </div>
        
        <div className="header-stats">
          <div className="stat-card clear">
            <div className="stat-icon">👷</div>
            <div className="stat-content">
              <div className="stat-value">{plombiers.length}</div>
              <div className="stat-label">Plombiers actifs</div>
            </div>
          </div>
          <div className="stat-card clear">
            <div className="stat-icon">🏗️</div>
            <div className="stat-content">
              <div className="stat-value">{totals.chantiers}</div>
              <div className="stat-label">Chantiers total</div>
            </div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-value">{formatCurrency(totals.chiffre)}</div>
              <div className="stat-label">Chiffre d'affaires</div>
            </div>
          </div>
        </div>
      </div>

      <div className="plombiers-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Rechercher un plombier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="date-filters">
          <div className="filter-type-selector">
            <span className="filter-label">📅 Filtrer par:</span>
            <select 
              value={filterType} 
              onChange={(e) => handleFilterTypeChange(e.target.value)}
              className="filter-select"
            >
              <option value="all">Toutes les données</option>
              <option value="day">Par jour</option>
              <option value="week">Par semaine</option>
              <option value="month">Par mois</option>
              <option value="year">Par année</option>
            </select>
          </div>
          
          {filterType === "day" && (
            <div className="date-input-group">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="date-input"
              />
            </div>
          )}
          
          {filterType === "week" && (
            <div className="date-input-group">
              <label className="week-label">Choisir une date dans la semaine:</label>
              <input
                type="date"
                onChange={(e) => handleWeekDateChange(e.target.value)}
                className="date-input"
                placeholder="Sélectionnez une date"
              />
              {selectedWeekStart && selectedWeekEnd && (
                <div className="week-range">
                  <span className="week-info">
                    📅 Semaine du {new Date(selectedWeekStart).toLocaleDateString('fr-FR')} 
                    au {new Date(selectedWeekEnd).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
            </div>
          )}
          
          {filterType === "month" && (
            <div className="date-input-group">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="month-select"
              >
                <option value="">Choisir un mois</option>
                <option value="1">Janvier</option>
                <option value="2">Février</option>
                <option value="3">Mars</option>
                <option value="4">Avril</option>
                <option value="5">Mai</option>
                <option value="6">Juin</option>
                <option value="7">Juillet</option>
                <option value="8">Août</option>
                <option value="9">Septembre</option>
                <option value="10">Octobre</option>
                <option value="11">Novembre</option>
                <option value="12">Décembre</option>
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="year-select"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}
          
          {filterType === "year" && (
            <div className="date-input-group">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="year-select"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}
          
          <div className="current-filter">
            <span className="filter-badge">{getFilterLabel()}</span>
          </div>
        </div>
        
        <button className="refresh-btn" onClick={() => { fetchPlombiers(); fetchGroups(); }}>
          🔄 Actualiser
        </button>
      </div>

      {/* Groups Section */}
      {groups && groups.length > 0 && (
        <div className="plombiers-table-wrapper" style={{ marginBottom: 24 }}>
          <h2 style={{ margin: '12px 0' }}>Groupes de Plombiers (travaillant ensemble)</h2>
          <table className="plombiers-table">
            <thead>
              <tr>
                <th>
                  <span>Groupe</span>
                </th>
                <th>
                  <span>Nombre de Chantiers</span>
                </th>
                <th>
                  <span>Chiffre d'Affaires</span>
                </th>
                <th>
                  <span>Marge Brute</span>
                </th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, index) => (
                <tr key={`grp-${index}`} className="plombier-row">
                  <td className="plombier-name">
                    <div className="name-cell">
                      <span className="plombier-avatar">👥</span>
                      <span className="name-text">{g.nom}</span>
                    </div>
                  </td>
                  <td className="chantiers-count">
                    <span className="count-badge">{g.nombreChantiers}</span>
                  </td>
                  <td className="chiffre-affaire">
                    <span className="amount positive">{formatCurrency(g.chiffreAffaire)}</span>
                  </td>
                  <td className="marge">
                    <div className="marge-cell">
                      <span className="marge-amount" style={{ color: g.marge >= 0 ? "#10b981" : "#ef4444" }}>
                        {formatCurrency(g.marge)}
                      </span>
                      <span className="marge-percent" style={{ color: getMargeColor(g.margePercent), fontWeight: 600 }}>
                        ({formatPercent(g.margePercent)})
                      </span>
                    </div>
                  </td>
                  <td className="performance">
                    <div className="performance-indicator">
                      {g.margePercent >= 30 ? (
                        <span className="perf-badge excellent">⭐ Excellent</span>
                      ) : g.margePercent >= 20 ? (
                        <span className="perf-badge good">✅ Bon</span>
                      ) : g.margePercent >= 10 ? (
                        <span className="perf-badge average">⚠️ Moyen</span>
                      ) : (
                        <span className="perf-badge poor">❌ Faible</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sortedAndFilteredPlombiers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Aucun plombier trouvé</h3>
          <p>
            {searchTerm
              ? "Aucun plombier ne correspond à votre recherche"
              : "Aucun plombier n'a été assigné aux chantiers"}
          </p>
        </div>
      ) : (
        <div className="plombiers-table-wrapper">
          <table className="plombiers-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("nom")} className="sortable">
                  <span>Nom du Plombier</span>
                  {sortField === "nom" && (
                    <span className="sort-indicator">
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th onClick={() => handleSort("nombreChantiers")} className="sortable">
                  <span>Nombre de Chantiers</span>
                  {sortField === "nombreChantiers" && (
                    <span className="sort-indicator">
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th onClick={() => handleSort("chiffreAffaire")} className="sortable">
                  <span>Chiffre d'Affaires</span>
                  {sortField === "chiffreAffaire" && (
                    <span className="sort-indicator">
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th onClick={() => handleSort("marge")} className="sortable">
                  <span>Marge Brute</span>
                  {sortField === "marge" && (
                    <span className="sort-indicator">
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredPlombiers.map((plombier, index) => (
                <tr key={index} className="plombier-row">
                  <td className="plombier-name">
                    <div className="name-cell">
                      <span className="plombier-avatar">👷</span>
                      <span className="name-text">{plombier.nom}</span>
                    </div>
                  </td>
                  <td className="chantiers-count">
                    <span className="count-badge">{plombier.nombreChantiers}</span>
                  </td>
                  <td className="chiffre-affaire">
                    <span className="amount positive">
                      {formatCurrency(plombier.chiffreAffaire)}
                    </span>
                  </td>
                  <td className="marge">
                    <div className="marge-cell">
                      <span
                        className="marge-amount"
                        style={{ color: plombier.marge >= 0 ? "#10b981" : "#ef4444" }}
                      >
                        {formatCurrency(plombier.marge)}
                      </span>
                      <span
                        className="marge-percent"
                        style={{
                          color: getMargeColor(plombier.margePercent),
                          fontWeight: 600,
                        }}
                      >
                        ({formatPercent(plombier.margePercent)})
                      </span>
                    </div>
                  </td>
                  <td className="performance">
                    <div className="performance-indicator">
                      {plombier.margePercent >= 30 ? (
                        <span className="perf-badge excellent">
                          ⭐ Excellent
                        </span>
                      ) : plombier.margePercent >= 20 ? (
                        <span className="perf-badge good">
                          ✅ Bon
                        </span>
                      ) : plombier.margePercent >= 10 ? (
                        <span className="perf-badge average">
                          ⚠️ Moyen
                        </span>
                      ) : (
                        <span className="perf-badge poor">
                          ❌ Faible
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {sortedAndFilteredPlombiers.length > 0 && (
              <tfoot>
                <tr className="totals-row">
                  <td className="total-label">Total</td>
                  <td className="total-value">
                    <span className="count-badge total">{totals.chantiers}</span>
                  </td>
                  <td className="total-value">
                    <span className="amount total">
                      {formatCurrency(totals.chiffre)}
                    </span>
                  </td>
                  <td className="total-value">
                    <div className="marge-cell">
                      <span
                        className="marge-amount total"
                        style={{ color: totals.marge >= 0 ? "#10b981" : "#ef4444" }}
                      >
                        {formatCurrency(totals.marge)}
                      </span>
                      <span
                        className="marge-percent"
                        style={{
                          color: getMargeColor(
                            totals.chiffre > 0 ? (totals.marge / totals.chiffre) * 100 : 0
                          ),
                          fontWeight: 700,
                        }}
                      >
                        ({formatPercent(
                          totals.chiffre > 0 ? (totals.marge / totals.chiffre) * 100 : 0
                        )})
                      </span>
                    </div>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default PlombiersList;
